import {useMutation, useQuery} from '@apollo/client';
import {Box} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Panel} from '../../components/Panel/Panel';
import {useQueryParams} from '../../hooks/useQueryParams';
import {
  OneViewModelTypesEnum,
  ReviewStatusOneviewEnum,
} from '../../schema-types/globalTypes';
import {
  GetNativeIdFromCuro,
  GetNativeIdFromCuroVariables,
} from '../../schema-types/GetNativeIdFromCuro';
import {
  CREATE_REVIEW,
  GET_ACCOUNT_REVIEWS,
} from '../../components/AccountOverview/accountOverviewQueries';
import {CreateReview, CreateReviewVariables} from '../../schema-types/CreateReview';
import {AccountReviews, AccountReviewsVariables} from '../../schema-types/AccountReviews';
import {GET_NATIVE_ID_FROM_CURO} from './TestHomepage';
import {useGetSppReview} from '../AccountInstructionsPage/AccountInstructionsPage';
import validateUUID from '../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

export const TestAnyOpenReviewPage: React.FC = () => {
  const {accountCuroId} = useParams();
  const [loading, setLoading] = useState(true);
  const params = useQueryParams();
  const curoId = validateUUID(accountCuroId);
  const {data: accountData, loading: accountLoading} = useQuery<
    GetNativeIdFromCuro,
    GetNativeIdFromCuroVariables
  >(GET_NATIVE_ID_FROM_CURO, {
    variables: {
      curoId: curoId!,
      modelName: OneViewModelTypesEnum.ACCOUNT,
    },
    skip: !curoId,
  });
  const [createReview, {loading: createReviewLoading, data: createReviewData}] =
    useMutation<CreateReview, CreateReviewVariables>(CREATE_REVIEW);
  const {loading: reviewsLoading, data: reviewsData} = useQuery<
    AccountReviews,
    AccountReviewsVariables
  >(GET_ACCOUNT_REVIEWS, {
    variables: {
      accountId: validateUUID(accountData?.nativeId)!,
      idType: 'id',
      onlyCurrent: true,
    },
    skip: !validateUUID(accountData?.nativeId),
  });

  useEffect(() => {
    async function go() {
      if (!accountData?.nativeId) {
        return;
      }
      const supplement = params.get('goTo') ? `/${params.get('goTo')}` : '';
      const reviewName = params.get('reviewName');
      const review = reviewsData?.reviews?.find(
        (r) => r.statusOneview === ReviewStatusOneviewEnum.OPEN
      );
      if (review) {
        navigate({
          pathname: `/account/${accountData?.nativeId}/review-journey/${review?.id}${supplement}`,
        });
      } else {
        createReview({
          variables: {
            accountId: accountData?.nativeId,
            reviewType: 'Ad-hoc',
            name: reviewName ? `Test: ${reviewName}` : 'Test review',
            ignoreLimit: true,
          },
        })
          .then((createReviewResult) => {
            if (createReviewResult.data?.createReview?.review?.id) {
              navigate({
                pathname: `/account/${accountData?.nativeId}/review-journey/${createReviewResult.data?.createReview?.review?.id}${supplement}`,
              });
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
    go();
  }, [reviewsData?.reviews, accountData?.nativeId]);
  const navigate = useNavigate();
  return (
    <div>
      <Panel title="Account test setup">
        Curo id: {accountCuroId}
        {reviewsLoading || reviewsLoading || loading ? <Box>Setting up test</Box> : null}
      </Panel>
    </div>
  );
};

export const TestInstruction: React.FC = () => {
  const {accountCuroId} = useParams();
  const [loading, setLoading] = useState(true);
  const params = useQueryParams();
  const curoId = validateUUID(accountCuroId);
  const {data: accountData, loading: accountLoading} = useQuery<
    GetNativeIdFromCuro,
    GetNativeIdFromCuroVariables
  >(GET_NATIVE_ID_FROM_CURO, {
    variables: {
      curoId: curoId!,
      modelName: OneViewModelTypesEnum.ACCOUNT,
    },
    skip: !curoId,
  });
  return (
    <div>
      {accountData?.nativeId ? (
        <TestInstructionInner accountId={accountData.nativeId} />
      ) : (
        <Panel title="Account test setup">
          {loading ? <Box>Setting up test</Box> : null}
        </Panel>
      )}
    </div>
  );
};

export const TestInstructionInner: React.FC<{accountId: string}> = ({accountId}) => {
  const {loading: reviewLoading, reviewId} = useGetSppReview(accountId);
  const {createRecommendUrl} = useRequirementsDentistOrBAU();
  useEffect(() => {
    async function go() {
      if (!reviewId) {
        return;
      }

      navigate({
        pathname: `/account/${accountId}/${createRecommendUrl}`,
      });
    }

    go();
  }, [accountId, reviewId]);
  const navigate = useNavigate();
  return (
    <div>
      <Panel title="Account test setup">
        {reviewLoading ? <Box>Setting up test</Box> : null}
      </Panel>
    </div>
  );
};
